import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { ErrorService } from '@/lib/errors/ErrorService';
import type {
  MessageFeedbackRating,
  MessageFeedbackReason,
  MessageFeedbackResponse,
} from '@hector/shared-types';

interface SubmitArgs {
  rating: MessageFeedbackRating;
  /** Réservé au pouce-bas — ignoré sur 'up'. */
  reason?: MessageFeedbackReason | null;
  comment?: string | null;
}

/**
 * API du feedback (pouce) sur un message assistant CD-v3.
 *
 * `submit` upsert le vote (idempotent côté backend) ; `remove` le retire (toggle off).
 * Les deux renvoient un booléen de succès pour permettre un revert optimiste côté UI.
 * Endpoint partagé avec Edition-v3 : `/case-discovery-v3/messages/:id/feedback`.
 */
export function useMessageFeedback(messageId: string) {
  const [pending, setPending] = useState(false);
  const queryClient = useQueryClient();

  const markConversationFeedbackStale = useCallback(() => {
    // The mounted button owns the optimistic state. Marking every identity-scoped
    // feedback query stale without refetching makes a later remount revalidate,
    // while avoiding an immediate response that could overwrite the current click.
    void queryClient.invalidateQueries({
      queryKey: ['case-discovery-v3', 'conversation-feedback'],
      refetchType: 'none',
    });
  }, [queryClient]);

  const submit = useCallback(
    async (args: SubmitArgs): Promise<boolean> => {
      setPending(true);
      try {
        await apiClient.post<MessageFeedbackResponse>(
          `/case-discovery-v3/messages/${messageId}/feedback`,
          {
            rating: args.rating,
            reason: args.reason ?? undefined,
            comment: args.comment ?? undefined,
          },
        );
        markConversationFeedbackStale();
        return true;
      } catch (error) {
        ErrorService.handle(error, {
          context: { action: `submit_message_feedback:${messageId}` },
        });
        return false;
      } finally {
        setPending(false);
      }
    },
    [markConversationFeedbackStale, messageId],
  );

  const remove = useCallback(async (): Promise<boolean> => {
    setPending(true);
    try {
      await apiClient.delete(`/case-discovery-v3/messages/${messageId}/feedback`);
      markConversationFeedbackStale();
      return true;
    } catch (error) {
      ErrorService.handle(error, {
        context: { action: `remove_message_feedback:${messageId}` },
      });
      return false;
    } finally {
      setPending(false);
    }
  }, [markConversationFeedbackStale, messageId]);

  return { submit, remove, pending };
}
